import type { CSSProperties } from 'react';
import { usePhotoPan, type PhotoPosition } from '../../hooks/usePhotoPan';

interface Props {
  src: string;
  /** ADR-0048: an id of the catalogue; an unknown one draws without a frame. */
  frame: string;
  /** Page width in mm: the frame is in mm of the printed page. */
  pageWidth: number;
  /** Which part of an overflowing photo shows; null = centred. */
  position: PhotoPosition | null;
  /** Set only on the selected slot: the photo can then be dragged around. */
  onMove?: (position: PhotoPosition) => void;
}

/** A photo in its slot, with the album's frame: none, a white border, rounded corners or a shadow. */
export default function AlbumFramedPhoto({ src, frame, pageWidth, position: saved, onMove }: Props) {
  const { position, handlers } = usePhotoPan(saved, onMove);
  const mm = (v: number) => `${(v / pageWidth) * 100}cqw`;
  const inner: CSSProperties =
    frame === 'white-border' ? { inset: 0, padding: mm(2.5), background: '#fff', boxShadow: `0 ${mm(0.4)} ${mm(1.2)} rgba(15, 23, 42, 0.18)` }
      : frame === 'rounded' ? { inset: 0, borderRadius: mm(3), overflow: 'hidden' }
        // The slot clips what overflows it: the shadow needs the photo inset a little to show.
        : frame === 'shadow' ? { inset: mm(1.2), boxShadow: `0 ${mm(0.8)} ${mm(2.4)} rgba(15, 23, 42, 0.35)` }
          : { inset: 0 };
  return (
    <div className="absolute" style={inner}>
      <img
        src={src}
        alt=""
        draggable={false}
        className={`w-full h-full object-cover ${onMove ? 'cursor-move touch-none' : ''}`}
        style={{ objectPosition: `${position.x * 100}% ${position.y * 100}%` }}
        {...handlers}
      />
    </div>
  );
}
